import {
    EntityActiveInterval,
    RatchetDefinitionsEnum,
    RatchetModel,
    RatchetModels,
    RoyaltyModelsEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { isUndefined, sortBy } from 'lodash';
import {
    ContractBreadcrumbTitle,
    FreeTrialPlan,
    PaperPlanGroup,
    RateCardStatus,
    RateMultiplierDisplayName,
    Status
} from 'src/constants';
import { PRECISION } from 'src/constants/rateCards';
import { ROUTES } from 'src/constants/routes';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IAddRateCardModalValues } from 'src/types/IRateCard';
import { getCurrentYearMonth } from 'src/util/dateTimeUtil';

import { getPromoDurationDisplayNumber } from './PaperPlans/PaperPlanUtils';
import { getGeneralStatus } from './grid/status';

export function isNumberOrNumberString(value: any): boolean {
    if (typeof value === 'string') {
        if (value.match(/^[-.0-9]+$/g)) {
            return true;
        } else {
            return false;
        }
    } else if (typeof value !== 'number') {
        return false;
    } else {
        return true;
    }
}

type RateRequirement = {
    name: string;
    condition: (rate: number) => boolean;
    models: string[];
};

const ROYALTY_MODEL_CODES = new Set(['ADREV', 'COGS', 'CPP', 'PSM', 'REV']);
/**
 * A list of conditions mapped to the models which must satisfy those conditions
 * to be considered 'valid'. A rate for a given model is only valid if all of
 * the conditions whose list contains that model evaluate to `true`.
 */
export const RATE_REQUIREMENTS: RateRequirement[] = [
    {
        name: 'greater than or equal to zero',
        condition: (rate) => rate >= 0,
        models: ['ADREV', 'COGS', 'CPP', 'PSM', 'REV']
    },
    { name: 'less than or equal to one', condition: (rate) => rate <= 1, models: ['ADREV', 'COGS', 'REV'] }
];

export function isValidRate(royaltyModelCode: string, rate: string | number): boolean {
    return satisfiesRequirements(royaltyModelCode, rate) === null;
}

export function satisfiesRequirements(royaltyModelCode: string, rate: string | number): string | null {
    if (!ROYALTY_MODEL_CODES.has(royaltyModelCode)) {
        return 'valid model code';
    }

    const numericRate = Number(rate);
    for (let item of RATE_REQUIREMENTS) {
        if (item.models.includes(royaltyModelCode) && !item.condition(numericRate)) {
            return item.name;
        }
    }

    return null;
}

export function formatRate(royaltyModelName: string, royaltyModelCode: string, rate: string | number): string {
    const numericRate = Number(rate);
    if (rate === null || rate === undefined || rate.toString().trim() === '') return '';
    if (numericRate || numericRate === 0) {
        if (royaltyModelName.includes('%')) {
            return (numericRate * 100).toFixed(PRECISION.PERCENTAGE) + '%';
        }
        return numericRate.toFixed(royaltyModelCode === 'CPP' ? PRECISION.CPP : PRECISION.DEFAULT);
    }
    return rate.toString();
}

export const buildRateCardName = (addRateCardModalValues: IAddRateCardModalValues, serviceName: string) => {
    const vendor = addRateCardModalValues.vendor?.toUpperCase() ?? '—';
    const service = serviceName?.toUpperCase() ?? '—';
    const dateYYYYMM = addRateCardModalValues.month || getCurrentYearMonth();
    addRateCardModalValues.month = dateYYYYMM;

    return `${vendor}_${service}_${dateYYYYMM}`;
};

export const getRateCardStatus = (rateCardValues: any) => {
    if (rateCardValues.isDeleted || rateCardValues.contractIsDeleted) {
        return RateCardStatus.Archived;
    } else if (rateCardValues.isDraft || rateCardValues.contractIsDraft) {
        return RateCardStatus.Draft;
    } else {
        if (rateCardValues.entityActiveIntervals && rateCardValues.entityActiveIntervals.length > 0) {
            const activeUpcomingDeals = rateCardValues.entityActiveIntervals
                .filter(
                    (entityActiveInterval: EntityActiveInterval) =>
                        entityActiveInterval.entityId && entityActiveInterval.entityName
                )
                .filter(
                    (entityActiveInterval: EntityActiveInterval) =>
                        getGeneralStatus(entityActiveInterval) === Status.Active ||
                        getGeneralStatus(entityActiveInterval) === Status.Upcoming
                );

            return activeUpcomingDeals.length > 0 ? RateCardStatus.Active : RateCardStatus.Inactive;
        } else {
            return RateCardStatus.Inactive;
        }
    }
};

export const getDerivedMultiplierName = (planObj: Record<string, string | boolean | number>): string => {
    const { rateType, paperPlanName, renewalCadence, planGroup, promoDurationInDays } = planObj;

    // Check for specific free trial plans
    if (
        rateType === RoyaltyModelsEnum.CPP &&
        [
            FreeTrialPlan.HAWKFIRE_FAMILY_90DAY_FREE_TRIAL,
            FreeTrialPlan.HAWKFIRE_KATANA_FAM_MONTHLY_90_DAY_FT,
            FreeTrialPlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT
        ].includes(paperPlanName as FreeTrialPlan)
    ) {
        return RateMultiplierDisplayName.NONE;
    }

    // Check for renewal cadence
    if (renewalCadence === RateMultiplierDisplayName.ANNUAL) {
        return RateMultiplierDisplayName.ANNUAL;
    }
    if (renewalCadence === RateMultiplierDisplayName.DAILY) {
        return RateMultiplierDisplayName.DAILY;
    }
    if (renewalCadence === RateMultiplierDisplayName.WEEKLY) {
        return RateMultiplierDisplayName.WEEKLY;
    }

    // Check for price drop plans
    if (planGroup === PaperPlanGroup.PriceDrop) {
        const promoDuration = getPromoDurationDisplayNumber(promoDurationInDays as string);
        if (promoDuration === 90) {
            return RateMultiplierDisplayName.PRICE_DROP_3M;
        }
        if (promoDuration === 120) {
            return RateMultiplierDisplayName.PRICE_DROP_4M;
        }
        if (promoDuration === 180) {
            return RateMultiplierDisplayName.PRICE_DROP_6M;
        }
    }

    // Check for extended trial or carrier intro plans
    if (planGroup === PaperPlanGroup.ExtendedTrial || planGroup === PaperPlanGroup.CarrierIntro) {
        const promoDuration = getPromoDurationDisplayNumber(promoDurationInDays as string);
        switch (promoDuration) {
            case 60:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_2M;
            case 90:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_3M;
            case 120:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_4M;
            case 150:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_5M;
            case 180:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_6M;
            default:
                return RateMultiplierDisplayName.NONE;
        }
    }

    // Default case
    return RateMultiplierDisplayName.NONE;
};

export const getRateCardBreadcrumbItems = (secondLevelTitle: string): IBreadcrumb[] => [
    { link: ROUTES.RATE_CARDS.INDEX, title: ContractBreadcrumbTitle.Contracts },
    { link: '', title: secondLevelTitle }
];

export const getConversionModel = (
    ratchetModels: RatchetModels | undefined,
    isTertiary = false,
    isGuardrail = false
): RatchetModel | undefined => {
    const sortedRatchetModels = sortBy(ratchetModels, ['orderId']);
    return sortedRatchetModels?.find((ratchetModel) => {
        if (isGuardrail) {
            return ratchetModel.ratchetModel?.includes('GUARDRAIL');
        }
        if (isTertiary) {
            return ratchetModel.ratchetModel?.includes('TERTIARY');
        }
        return !ratchetModel.ratchetModel?.includes('TERTIARY');
    });
};

export const getCamelStrFromAllCaps = (allCapsStr: string, leadingStrToSkip = ''): string => {
    const wordArr = allCapsStr
        .split('_')
        .map((word) =>
            leadingStrToSkip && word === leadingStrToSkip ? word : word.charAt(0) + word.substring(1).toLowerCase()
        );

    return wordArr.join(' ');
};

export const hasNonEmptyNumberValue = (value: number | string | undefined): boolean => {
    if (isUndefined(value) || value === '' || isNaN(Number(value))) {
        return false;
    } else {
        return true;
    }
};

export const formatDefinitionPercentageValue = (value: string | undefined, type: string): string => {
    if (!value) return '-';

    // Check if the type is related to percentages and the value is a number
    const shouldFormatAsPercentage = type.includes(RatchetDefinitionsEnum.SECONDARY_MONTHLY_CONVERSION_RATIO_THRESHOLD);

    if (shouldFormatAsPercentage) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            return `${(numValue * 100).toFixed(1)}%`;
        }
    }
    return value;
};
